import { useEffect } from 'react';
import { PictureInPicture2, Timer } from 'lucide-react';
import { useAuth } from '../auth/AuthContext.tsx';
import { useMiniTimerWindow } from '../timer/MiniTimerWindow.tsx';
import { formatElapsed, useTimer } from '../timer/useTimer.ts';

/**
 * Header pill. Shows the running timer at a glance and pops out the dockable
 * mini window. Always rendered for a signed-in agent so there's an entry point
 * whether or not a timer is going.
 */
export function TimerIndicator(): React.ReactElement | null {
  const { agent } = useAuth();
  const { timer } = useTimer();
  const mini = useMiniTimerWindow();

  // Ctrl/Cmd+Shift+K toggles the mini window, mirroring GlobalSearch's ⌘K.
  useEffect(() => {
    if (!agent) return;
    function handler(e: KeyboardEvent) {
      const isMac = navigator.platform.toLowerCase().includes('mac');
      const combo = (isMac ? e.metaKey : e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'k';
      if (!combo) return;
      e.preventDefault();
      if (mini.isOpen) mini.close();
      else void mini.open();
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [agent, mini]);

  if (!agent) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => void mini.open()}
        title={
          mini.supported
            ? 'Pop out the timer (Ctrl+Shift+K) — floats above other windows'
            : 'Open the timer in a separate window (Ctrl+Shift+K)'
        }
        className={
          'inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold text-white transition ' +
          (timer
            ? 'bg-[var(--color-t3-blue)]/20 hover:bg-[var(--color-t3-blue)]/30'
            : 'bg-white/10 text-white/80 hover:bg-white/20 hover:text-white')
        }
      >
        <Timer className="h-3.5 w-3.5" />
        {timer ? (
          <>
            <span className="font-mono">{formatElapsed(timer.startedAt)}</span>
            <span className="text-white/80">
              {timer.ticketId ? `T-${timer.ticketDisplayNumber}` : (timer.organizationName ?? 'Internal')}
            </span>
          </>
        ) : (
          <span>Start timer</span>
        )}
        <PictureInPicture2 className="h-3.5 w-3.5 opacity-70" />
      </button>
      {mini.portal}
    </>
  );
}
